/**
 * Prompt for installation date when moving a deal to a capture stage outside Handover flow.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePickerPopover } from "@/components/ui/date-picker-popover";
import { Loader2 } from "lucide-react";
import { todayYmdLocal } from "@/lib/crm-deal-stage";

export interface InstallationDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealTitle?: string;
  defaultDate?: string;
  onConfirm: (installationDate: string) => void | Promise<void>;
  confirming?: boolean;
}

export function InstallationDateDialog({
  open,
  onOpenChange,
  dealTitle,
  defaultDate,
  onConfirm,
  confirming = false,
}: InstallationDateDialogProps) {
  const { t } = useTranslation();
  const [date, setDate] = useState(defaultDate || todayYmdLocal());

  useEffect(() => {
    if (open) setDate(defaultDate || todayYmdLocal());
  }, [open, defaultDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("crm.installationDate.title")}</DialogTitle>
          <DialogDescription>
            {dealTitle
              ? t("crm.installationDate.descriptionNamed", { deal: dealTitle })
              : t("crm.installationDate.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="installation-date-picker">{t("crm.installationDate.label")}</Label>
          <DatePickerPopover
            id="installation-date-picker"
            value={date}
            onChange={(v) => setDate(v || todayYmdLocal())}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={confirming}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            disabled={confirming || !date}
            onClick={() => void onConfirm(date)}
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
